'use client';

import { forwardRef } from 'react';
import type {
  TableProps as MantineTableProps,
  TableTbodyProps,
  TableTdProps,
  TableTfootProps,
  TableTheadProps,
  TableThProps,
  TableTrProps,
} from '@mantine/core';
import { Table as MantineTable } from '@mantine/core';

import type { LockedProps } from '../types';

// The lock is a plain `Omit` on every part: Mantine's Table components are ordinary factories, not
// polymorphic, so there is no intrinsic-element prop union to re-admit `className`/`style`. Sizing,
// alignment and background move to Box style props (`w`, `ta`, `bg`), which the lock leaves open.
// `withTableBorder`/`withColumnBorders`/`withRowBorders` also stay open — border presence is a
// per-table layout decision here, unlike the elevation props `LockedProps` covers.
const Table = forwardRef<HTMLTableElement, Omit<MantineTableProps, LockedProps>>((props, ref) => (
  <MantineTable ref={ref} {...props} />
));
Table.displayName = 'Table';

// The section/row/cell components wrap Mantine's compound Table parts so they
// pick up the v7 Table styling context (plain thead/tr/td would render unstyled).
const TableHeader = forwardRef<HTMLTableSectionElement, Omit<TableTheadProps, LockedProps>>(
  (props, ref) => <MantineTable.Thead ref={ref} {...props} />,
);
TableHeader.displayName = 'TableHeader';

const TableFooter = forwardRef<HTMLTableSectionElement, Omit<TableTfootProps, LockedProps>>(
  (props, ref) => <MantineTable.Tfoot ref={ref} {...props} />,
);
TableFooter.displayName = 'TableFooter';

const TableBody = forwardRef<HTMLTableSectionElement, Omit<TableTbodyProps, LockedProps>>(
  (props, ref) => <MantineTable.Tbody ref={ref} {...props} />,
);
TableBody.displayName = 'TableBody';

const TableRow = forwardRef<HTMLTableRowElement, Omit<TableTrProps, LockedProps>>((props, ref) => (
  <MantineTable.Tr ref={ref} {...props} />
));
TableRow.displayName = 'TableRow';

/** Exported so callers that forward column sizing can `Pick` from it instead of restating it. */
export type TableHeadProps = Omit<TableThProps, LockedProps>;

const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>((props, ref) => (
  <MantineTable.Th ref={ref} {...props} />
));
TableHead.displayName = 'TableHead';

const TableCell = forwardRef<HTMLTableCellElement, Omit<TableTdProps, LockedProps>>(
  (props, ref) => <MantineTable.Td ref={ref} {...props} />,
);
TableCell.displayName = 'TableCell';

export { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow };
